import { Geometry } from "features/onboarding/visuals/live/geometry";
import {
  clamp01,
  easeOut,
  FADE_IN,
  FADE_OUT,
  LevelView,
  Story,
  TONE,
} from "features/onboarding/visuals/live/story";

const NS = "http://www.w3.org/2000/svg";
type Attrs = Record<string, string | number>;

const el = <K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  parent?: Element
): SVGElementTagNameMap[K] => {
  const node = document.createElementNS(NS, tag);
  for (const key in attrs) node.setAttribute(key, String(attrs[key]));
  parent?.appendChild(node);
  return node;
};
const set = (node: Element, attrs: Attrs) => {
  for (const key in attrs) node.setAttribute(key, String(attrs[key]));
};

/** A badge's pop: a small overshoot, then rest. */
const spring = (x: number): number => {
  const k = clamp01(x);
  const c1 = 1.4;
  const c3 = c1 + 1;
  return 1 + c3 * (k - 1) ** 3 + c1 * (k - 1) ** 2;
};

/** Lucide's bell, drawn at 24 units. */
const BELL =
  "M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0";

interface LevelNodes {
  line: SVGLineElement;
  flash: SVGLineElement;
  label: SVGGElement;
  dot: SVGCircleElement;
  name: SVGTSpanElement;
  price: SVGTSpanElement;
}

/**
 * Draws a story into an SVG at 1:1 with CSS pixels: the order lines and their
 * right-axis labels, the price scrolling like a live feed ("now" at ~74% of
 * the plot, the past sliding left and fading at the left edge), a price tag
 * that follows the price, fill badges with a soft ripple, a line flash and a
 * "+$x" chip. `render(t)` paints the frame for time t.
 */
export function createChart(
  svg: SVGSVGElement,
  story: Story,
  g: Geometry,
  uid: string
): { render: (t: number) => void } {
  svg.textContent = "";
  set(svg, { viewBox: `0 0 ${g.W} ${g.H}`, width: g.W, height: g.H });
  const { R, x0: X0, px: PX, top, bottom, font } = g;
  const fixed = g.mode === "fixed";
  // Seconds of the past on screen (all of it in the fixed window).
  const pastShown = g.mode === "fixed" ? Infinity : g.x0 / g.px;
  let [lo, hi] = story.rangeAt?.(0, pastShown) ?? story.range;
  const y = (v: number) => bottom - ((v - lo) / (hi - lo)) * (bottom - top);
  // Where a moment sits: fixed in place, or sliding left as time goes on.
  const xAt = (now: number, at: number) =>
    fixed ? X0 + at * PX : X0 - (now - at) * PX;

  const defs = el("defs", {}, svg);
  const area = el(
    "linearGradient",
    { id: `${uid}-area`, x1: 0, y1: 0, x2: 0, y2: 1 },
    defs
  );
  el(
    "stop",
    { offset: 0, "stop-color": "var(--viz-price)", "stop-opacity": 0.1 },
    area
  );
  el(
    "stop",
    { offset: 1, "stop-color": "var(--viz-price)", "stop-opacity": 0 },
    area
  );
  const fadeL = el("linearGradient", { id: `${uid}-fade`, x1: 0, x2: 1 }, defs);
  el(
    "stop",
    { offset: 0, "stop-color": "var(--viz-card)", "stop-opacity": 1 },
    fadeL
  );
  el(
    "stop",
    { offset: 1, "stop-color": "var(--viz-card)", "stop-opacity": 0 },
    fadeL
  );
  const clip = el("clipPath", { id: `${uid}-plot` }, defs);
  el("rect", { x: 0, y: 0, width: R, height: g.H }, clip);

  const scene = el("g", {}, svg);
  const linesLayer = el("g", {}, scene);
  const plot = el("g", { "clip-path": `url(#${uid}-plot)` }, scene);
  const areaPath = el("path", { fill: `url(#${uid}-area)` }, plot);
  // Fixed: the past is drawn from the start, faint; the live line grows from it.
  const pastPath = el(
    "path",
    {
      fill: "none",
      stroke: "var(--viz-price)",
      "stroke-opacity": 0.35,
      "stroke-width": 2,
      "stroke-linejoin": "round",
      "stroke-linecap": "round",
    },
    plot
  );
  const pricePath = el(
    "path",
    {
      "data-price": "",
      fill: "none",
      stroke: "var(--viz-price)",
      "stroke-width": 2,
      "stroke-linejoin": "round",
      "stroke-linecap": "round",
    },
    plot
  );
  const markers = el("g", {}, plot);
  const badges = el("g", {}, plot);
  if (!fixed)
    el(
      "rect",
      { x: 0, y: 0, width: 48, height: g.H, fill: `url(#${uid}-fade)` },
      scene
    );
  // The past (fixed): from the left edge to time 0, every 2px.
  let past = "";
  const drawPast = () => {
    past = "";
    if (fixed)
      for (let x = 0; x <= X0 + 1e-6; x += 2)
        past += `${past ? "L" : "M"}${x.toFixed(2)},${y(
          story.price((x - X0) / PX)
        ).toFixed(2)}`;
    pastPath.setAttribute("d", past);
  };
  drawPast();
  const axis = el("g", {}, scene);

  // Every level the story ever shows gets its nodes once.
  const keys: string[] = [];
  for (let t = 0; t <= story.loop; t += 0.1)
    story.levels(t).forEach((l) => {
      if (!keys.includes(l.key)) keys.push(l.key);
    });
  const nodes = new Map<string, LevelNodes>();
  keys.forEach((key) => {
    const line = el("line", { x1: 0, "stroke-width": 1 }, linesLayer);
    const flash = el(
      "line",
      { x1: 0, x2: R, "stroke-width": 2, opacity: 0 },
      linesLayer
    );
    const label = el("g", {}, axis);
    const dot = el("circle", { r: 3.5, cx: 0, cy: 0 }, label);
    const text = el(
      "text",
      { x: 10, y: font * 0.36, "font-size": font },
      label
    );
    const name = el("tspan", { "font-weight": 600 }, text);
    const price = el("tspan", {}, text);
    nodes.set(key, { line, flash, label, dot, name, price });
  });

  const guide = el(
    "line",
    {
      stroke: "var(--viz-price)",
      "stroke-opacity": 0.45,
      "stroke-dasharray": "2 3",
      "stroke-width": 1,
    },
    scene
  );
  const head = el("g", {}, scene);
  const ripple = el("circle", { r: 6, fill: "var(--viz-price)" }, head);
  el(
    "circle",
    {
      r: 4.5,
      fill: "var(--viz-card)",
      stroke: "var(--viz-price)",
      "stroke-width": 2,
    },
    head
  );
  const tag = el("g", {}, scene);
  const tagRect = el(
    "rect",
    { x: 0, y: -10, height: 20, rx: 6, fill: "var(--viz-price)" },
    tag
  );
  const tagText = el(
    "text",
    {
      y: font * 0.36,
      "text-anchor": "middle",
      "font-size": font,
      "font-weight": 600,
      fill: "var(--viz-on-solid)",
    },
    tag
  );
  const pops = el("g", {}, scene);

  // Chips size to their text (measured once per text).
  const widths = new Map<string, number>();
  const measure = el(
    "text",
    { "font-size": font, "font-weight": 600, opacity: 0 },
    svg
  );
  const width = (text: string) => {
    if (!widths.has(text)) {
      measure.textContent = text;
      const w = measure.getComputedTextLength() || text.length * font * 0.6;
      widths.set(text, w);
    }
    return widths.get(text)!;
  };
  const chip = (
    parent: Element,
    x: number,
    cy: number,
    text: string,
    tone: "up" | "stop",
    opacity: number
  ) => {
    const w = width(text) + 18;
    const left = Math.min(Math.max(x - w / 2, 4), R - w - 4);
    const group = el(
      "g",
      { transform: `translate(${left},${cy})`, opacity: opacity.toFixed(3) },
      parent
    );
    el(
      "rect",
      {
        x: 0,
        y: -11,
        width: w,
        height: 22,
        rx: 6,
        fill: "var(--viz-card)",
      },
      group
    );
    el(
      "rect",
      {
        x: 0,
        y: -11,
        width: w,
        height: 22,
        rx: 6,
        fill: tone === "up" ? "var(--viz-up-soft)" : "var(--viz-card)",
        stroke: TONE[tone],
        "stroke-opacity": tone === "up" ? 0.25 : 0.6,
      },
      group
    );
    const label = el(
      "text",
      {
        x: w / 2,
        y: font * 0.36,
        "text-anchor": "middle",
        "font-size": font,
        "font-weight": 600,
        fill: TONE[tone],
      },
      group
    );
    label.textContent = text;
  };

  const lineStyle = (l: LevelView): Attrs => {
    switch (l.state) {
      case "pending":
        return {
          stroke: TONE[l.tone],
          "stroke-opacity": 0.6,
          "stroke-dasharray": "none",
        };
      case "filled":
        return {
          stroke: TONE[l.tone],
          "stroke-opacity": 0.32,
          "stroke-dasharray": "3 4",
        };
      case "line":
        return {
          stroke: "var(--viz-ink-2)",
          "stroke-opacity": 0.5,
          "stroke-dasharray": "6 4",
        };
      default:
        return {
          stroke: "var(--viz-ink-3)",
          "stroke-opacity": 0.45,
          "stroke-dasharray": "3 4",
        };
    }
  };

  const render = (t: number) => {
    // The plot rescales as the story asks (everything below reads y()).
    const next = story.rangeAt?.(t, pastShown);
    if (next && (next[0] !== lo || next[1] !== hi)) {
      [lo, hi] = next;
      drawPast();
    }
    scene.style.opacity = String(
      t > story.loop - FADE_OUT
        ? Math.max(0, (story.loop - t) / FADE_OUT)
        : t < FADE_IN
        ? t / FADE_IN
        : 1
    );
    // Fixed: the line rests just short of the edge after the last event.
    const tc = fixed ? Math.min(t, g.stop) : t;
    const hx = fixed ? X0 + tc * PX : X0;
    const p = story.price(tc);
    const hy = y(p);

    // Order lines and their axis labels.
    const levels = story.levels(t);
    const shown = new Set(levels.map((l) => l.key));
    nodes.forEach((n, key) => {
      if (shown.has(key)) return;
      n.line.setAttribute("opacity", "0");
      n.flash.setAttribute("opacity", "0");
      n.label.setAttribute("opacity", "0");
    });
    const labels = levels
      .filter((l) => l.shown > 0.01)
      .map((l) => ({ l, at: y(l.value) }))
      .sort((a, b) => a.at - b.at);
    // Labels never overlap: push them apart, then keep them in the chart.
    const gap = font + 5;
    for (let pass = 0; pass < 4; pass++) {
      for (let i = 1; i < labels.length; i++)
        if (labels[i].at - labels[i - 1].at < gap) {
          const mid = (labels[i].at + labels[i - 1].at) / 2;
          labels[i - 1].at = mid - gap / 2;
          labels[i].at = mid + gap / 2;
        }
      labels.forEach((it) => {
        it.at = Math.min(Math.max(it.at, top - 4), bottom + 6);
      });
    }
    const labelled = new Set(labels.map((it) => it.l.key));
    nodes.forEach((n, key) => {
      if (!labelled.has(key)) n.label.setAttribute("opacity", "0");
    });
    levels.forEach((l) => {
      const n = nodes.get(l.key)!;
      const ly = y(l.value);
      set(n.line, {
        y1: ly,
        y2: ly,
        x2: R * easeOut(l.shown),
        opacity: l.fade ?? 1,
        ...lineStyle(l),
      });
      set(n.flash, { y1: ly, y2: ly });
    });
    labels.forEach(({ l, at }) => {
      const n = nodes.get(l.key)!;
      const tone =
        l.state === "empty"
          ? "var(--viz-ink-3)"
          : l.state === "line"
          ? "var(--viz-ink-2)"
          : TONE[l.tone];
      const solid = l.state === "pending";
      set(n.dot, {
        fill: solid ? tone : "var(--viz-card)",
        stroke: tone,
        "stroke-width": solid ? 0 : 1.3,
        r: solid ? 3.5 : 3,
      });
      const name = (g.compact && l.short) || l.name;
      n.name.textContent = name
        ? `${name}${l.state === "filled" ? " ✓" : ""}${g.compact ? "" : " "}`
        : "";
      set(n.name, {
        fill: tone,
        "fill-opacity": l.state === "empty" ? 0.8 : 1,
      });
      // A phone's axis names the level; the moving tag carries the price.
      n.price.textContent = g.compact && name ? "" : story.format(l.value);
      set(n.price, {
        fill:
          solid || l.state === "line" ? "var(--viz-ink-2)" : "var(--viz-ink-3)",
      });
      // The price tag (20px tall) hides the label it passes over.
      const near = clamp01((Math.abs(at - hy) - 16) / 6);
      set(n.label, {
        transform: `translate(${R + 14},${at.toFixed(1)})`,
        opacity: (clamp01(l.shown) * (l.fade ?? 1) * near).toFixed(3),
      });
    });

    // The price, sampled every 2px (and at "now"), at 1/100 px. Scroll: the
    // window of history ending at "now". Fixed: from time 0 to "now".
    const dt = 2 / PX;
    let d = "";
    let start = 0;
    if (fixed) {
      for (let k = 0; X0 + k * 2 < hx - 1; k++)
        d += `${d ? "L" : "M"}${(X0 + k * 2).toFixed(2)},${y(
          story.price(k * dt)
        ).toFixed(2)}`;
      d += `${d ? "L" : "M"}${hx.toFixed(2)},${hy.toFixed(2)}`;
      start = X0;
    } else {
      const count = Math.ceil((X0 + 24) / 2);
      start = X0 - count * 2;
      for (let k = count; k >= 0; k--)
        d += `${d ? "L" : "M"}${(X0 - k * 2).toFixed(2)},${y(
          story.price(t - k * dt)
        ).toFixed(2)}`;
    }
    pricePath.setAttribute("d", d);
    areaPath.setAttribute(
      "d",
      fixed
        ? `${past}L${d.slice(1)}L${hx.toFixed(2)},${bottom}L0,${bottom}Z`
        : `${d}L${hx},${bottom}L${start.toFixed(1)},${bottom}Z`
    );
    head.setAttribute(
      "transform",
      `translate(${hx.toFixed(2)},${hy.toFixed(2)})`
    );
    const beat = (((t % 1.6) + 1.6) % 1.6) / 1.6;
    set(ripple, { r: 5 + beat * 9, opacity: (0.2 * (1 - beat)).toFixed(3) });
    set(guide, { x1: hx + 8, x2: R + 4, y1: hy, y2: hy });
    const priceText = story.format(p);
    const tw = width(priceText) + 16;
    set(tagRect, { width: tw });
    set(tagText, { x: tw / 2 });
    tagText.textContent = priceText;
    tag.setAttribute("transform", `translate(${R + 6},${hy.toFixed(1)})`);

    // Alerts: a bell on the line where it fired.
    markers.textContent = "";
    story.markers?.forEach((m) => {
      if (t < m.t) return;
      const age = t - m.t;
      const mx = xAt(t, m.t);
      if (mx < -20) return;
      const my = y(m.value) - 20;
      if (age < 0.9)
        el(
          "circle",
          {
            cx: mx,
            cy: my,
            r: 9 + age * 16,
            fill: "none",
            stroke: "var(--viz-gold)",
            "stroke-opacity": (0.35 * (1 - age / 0.9)).toFixed(3),
          },
          markers
        );
      const bell = el(
        "g",
        {
          transform: `translate(${mx},${my}) scale(${spring(age / 0.5).toFixed(
            3
          )})`,
        },
        markers
      );
      el(
        "circle",
        {
          r: 9,
          fill: "var(--viz-card)",
          stroke: "var(--viz-gold)",
          "stroke-width": 1.4,
        },
        bell
      );
      el(
        "path",
        {
          d: BELL,
          transform: "translate(-5.4,-5.6) scale(0.45)",
          fill: "none",
          stroke: "var(--viz-gold)",
          "stroke-width": 2.4,
          "stroke-linecap": "round",
          "stroke-linejoin": "round",
        },
        bell
      );
    });

    // Fills: badges ride with the chart, the line flashes, a profit chip floats up.
    badges.textContent = "";
    pops.textContent = "";
    nodes.forEach((n) => n.flash.setAttribute("opacity", "0"));
    story.fills.forEach((f, index) => {
      if (t < f.t) return;
      const age = t - f.t;
      const bx = xAt(t, f.t);
      const by = y(f.value);
      const tone = TONE[f.tone];
      if (bx > -20) {
        const badge = el(
          "g",
          {
            transform: `translate(${bx.toFixed(1)},${by.toFixed(
              1
            )}) scale(${spring(age / 0.5).toFixed(3)})`,
          },
          badges
        );
        if (age < 0.8)
          el(
            "circle",
            {
              r: 7 + age * 16,
              fill: "none",
              stroke: tone,
              "stroke-width": 1.2,
              "stroke-opacity": (0.3 * (1 - age / 0.8)).toFixed(3),
            },
            badge
          );
        if (f.letter) {
          el(
            "circle",
            {
              r: 7.5,
              fill: tone,
              "fill-opacity": 0.92,
              stroke: "var(--viz-card)",
              "stroke-width": 1.5,
            },
            badge
          );
          const letter = el(
            "text",
            {
              y: 3,
              "text-anchor": "middle",
              "font-size": 8.5,
              "font-weight": 700,
              fill: "var(--viz-card)",
            },
            badge
          );
          letter.textContent = f.letter;
        } else {
          el(
            "circle",
            {
              r: 6.5,
              fill: "var(--viz-card)",
              stroke: tone,
              "stroke-width": 1.8,
            },
            badge
          );
          el("circle", { r: 2.6, fill: tone }, badge);
        }
      }
      const flash = nodes.get(f.level)?.flash;
      if (flash && age < 0.9)
        set(flash, {
          stroke: tone,
          opacity: (0.6 * (1 - age / 0.9)).toFixed(3),
        });
      if (f.profit && age < 1.8) {
        const op = age < 0.25 ? age / 0.25 : 1 - Math.max(0, (age - 1.1) / 0.7);
        chip(pops, bx, by - 22 - easeOut(age / 1.8) * 10, f.profit, "up", op);
      }
      // The result takes the profit chip's place once it has gone, so the
      // two never stack over the line (below only when there is no room).
      if (story.result?.fill === index && age > 1.6) {
        const above = by - 32 > top + 4;
        chip(
          pops,
          bx,
          above ? by - 32 : by + 26,
          story.result.text,
          "up",
          clamp01((age - 1.6) / 0.4)
        );
      }
    });

    // A short note by a level's label (the stop moved to breakeven).
    const note = story.note;
    if (note && t >= note.from && t <= note.to) {
      const level = levels.find((l) => l.key === note.level);
      if (level) {
        const op = Math.min(
          clamp01((t - note.from) / 0.3),
          clamp01((note.to - t) / 0.3)
        );
        const w = width(note.text) + 18;
        chip(pops, R - 10 - w / 2, y(level.value) - 18, note.text, "stop", op);
      }
    }
  };

  return { render };
}
